import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { getChinaCart } from '../api/mercuryx';

// Shared store for the MercuryX cart: count, items, and open state so the
// FAB, product page and drawer all read from the same source.
const ChinaCartContext = createContext(null);

export function ChinaCartProvider({ children }) {
    const [open, setOpen] = useState(false);
    const [count, setCount] = useState(0);
    const [items, setItems] = useState([]);
    // True while the home page is showing its Mercuryx (China) tab. The FAB
    // reads this so the cart button only appears on the home page when the
    // user is actually browsing Mercuryx ads — not on regular/dealer tabs.
    const [homeChinaActive, setHomeChinaActive] = useState(false);

    const refresh = useCallback(async () => {
        if (!localStorage.getItem('access_token')) {
            setCount(0);
            setItems([]);
            return;
        }
        try {
            const c = await getChinaCart();
            setCount(c?.count ?? 0);
            setItems(c?.items ?? []);
        } catch {
            setCount(0);
            setItems([]);
        }
    }, []);

    // Best-effort initial load. Unauthenticated users get 401 → silently 0.
    useEffect(() => {
        refresh();
    }, [refresh]);

    const value = useMemo(
        () => ({
            open,
            count,
            items,
            homeChinaActive,
            openCart: () => setOpen(true),
            closeCart: () => setOpen(false),
            setCount,
            setHomeChinaActive,
            refresh,
        }),
        [open, count, items, homeChinaActive, refresh],
    );

    return <ChinaCartContext.Provider value={value}>{children}</ChinaCartContext.Provider>;
}

export function useChinaCart() {
    const ctx = useContext(ChinaCartContext);
    if (!ctx) throw new Error('useChinaCart must be used inside ChinaCartProvider');
    return ctx;
}
